import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  ReferenceLine,
} from "recharts";
import { ArrowRight, Sparkles, Brain } from "lucide-react";

const riskColor = (v) => {
  if (v >= 0.8) return "hsl(var(--destructive))";
  if (v >= 0.6) return "hsl(var(--warning))";
  if (v >= 0.4) return "hsl(var(--primary))";
  if (v >= 0.2) return "hsl(217 91% 60%)";
  return "hsl(var(--muted-foreground))";
};

const probColor = (p) => {
  if (p >= 0.8) return "hsl(var(--destructive))";
  if (p >= 0.6) return "hsl(var(--warning))";
  return "hsl(var(--primary))";
};

const ForecastChart = ({ data = [], currentRisk = 0 }) => {
  const series = data.map((d) => ({
    horizon: d.horizon_min === 0 ? "Now" : `+${d.horizon_min}m`,
    risk: Math.round(d.risk * 100),
  }));
  return (
    <div className="h-44" data-testid="predictive-forecast-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
          <defs>
            <linearGradient id="riskFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="horizon" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} unit="%" width={32} domain={[0, 100]} />
          <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
          <ReferenceLine y={60} stroke="hsl(var(--warning))" strokeDasharray="3 3" strokeOpacity={0.6} />
          <ReferenceLine y={80} stroke="hsl(var(--destructive))" strokeDasharray="3 3" strokeOpacity={0.7} />
          <Area type="monotone" dataKey="risk" stroke="hsl(var(--destructive))" fill="url(#riskFill)" strokeWidth={1.75} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-3 -mt-1 text-[10px] font-mono text-muted-foreground">
        <span>Current Risk: <span className="text-foreground font-bold">{Math.round(currentRisk * 100)}</span></span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[hsl(var(--warning))]" /> 60 Watch</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[hsl(var(--destructive))]" /> 80 Alarm</span>
      </div>
    </div>
  );
};

const RiskMatrix = ({ rows = [] }) => {
  if (rows.length === 0) {
    return <div className="text-[11px] text-muted-foreground py-4 text-center">No zones to analyze.</div>;
  }
  const horizons = rows[0]?.cells.map((c) => c.horizon_min) || [];
  return (
    <div className="overflow-x-auto" data-testid="predictive-risk-matrix">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-wide border-b border-border">
            <th className="py-1.5 px-2 font-medium">Zone</th>
            {horizons.map((h) => (
              <th key={h} className="py-1.5 px-2 font-medium text-center">+{h}m</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.zone_id}
              className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
              data-testid={`predictive-matrix-row-${r.zone_id}`}
            >
              <td className="py-1.5 px-2 truncate max-w-[140px]">{r.zone_name}</td>
              {r.cells.map((c) => {
                const pct = Math.round(c.risk * 100);
                const color = riskColor(c.risk);
                return (
                  <td key={c.horizon_min} className="py-1 px-1 text-center">
                    <div
                      className="inline-block min-w-[44px] py-1 px-2 font-mono text-[10px] font-bold"
                      style={{
                        background: `${color}`,
                        color: "hsl(var(--background))",
                        opacity: 0.3 + c.risk * 0.7,
                      }}
                    >
                      {pct}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PredictionTable = ({ rows = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]" data-testid="predictive-table">
      <thead>
        <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-wide border-b border-border">
          <th className="py-1.5 px-2 font-medium">Predicted Event</th>
          <th className="py-1.5 px-2 font-medium">Zone</th>
          <th className="py-1.5 px-2 font-medium text-right">Probability</th>
          <th className="py-1.5 px-2 font-medium text-right">ETA</th>
          <th className="py-1.5 px-2 font-medium">Recommended Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
            data-testid={`predictive-table-row-${r.id}`}
          >
            <td className="py-1.5 px-2 font-medium truncate max-w-[180px]">{r.event}</td>
            <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[120px]">{r.zone_name}</td>
            <td className="py-1.5 px-2 text-right">
              <span
                className="inline-block px-1.5 py-0.5 font-mono text-[10px] font-bold"
                style={{
                  background: probColor(r.probability),
                  color: "hsl(var(--background))",
                }}
              >
                {Math.round(r.probability * 100)}%
              </span>
            </td>
            <td className="py-1.5 px-2 text-right font-mono">+{r.eta_min}m</td>
            <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[260px]">{r.action}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PredictiveInsightsPanel = ({ data }) => {
  const navigate = useNavigate();
  const d = data || { forecast_curve: [], current_risk: 0, risk_matrix: [], predictions: [] };

  return (
    <section className="space-y-3" data-testid="dashboard-section-predictive">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold tracking-tight text-xs">Predictive AI Insights</h2>
        </div>
        <button
          onClick={() => navigate("/predictive")}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="predictive-open-page-btn"
        >
          Open Predictive <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Risk Forecast (Next 60m)
          </div>
          <ForecastChart data={d.forecast_curve} currentRisk={d.current_risk} />
        </div>
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Brain className="w-3 h-3" /> AI Risk Matrix (Zone × Horizon)
          </div>
          <RiskMatrix rows={d.risk_matrix} />
        </div>
      </div>

      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget">Predicted Alert Table</div>
          <span className="micro-label">Top {d.predictions.length} Forecasts</span>
        </div>
        <PredictionTable rows={d.predictions} />
      </div>
    </section>
  );
};

export default PredictiveInsightsPanel;
